import { memo, useEffect, useState } from "react";
import { CalculationPriceDetailCardProps } from "./types";
import { Loader } from "ui/components/Loader";
import { useHandleDropdownChange } from "services/forms/hooks";
import { Pagination } from "services/search";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { getLeasingsAPI } from "services/api/calculations/leasing/api";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { NumberField } from "ui/components/TextField/NumberField";
import { formatCurrency, useTranslate } from "helpers";
import { LeasingRow } from "services/api/calculations/leasing/types";
import {
  CalculationEditFormValues,
  CalculationPrices,
} from "services/api/calculations/types";

/** Konfiguracija svih ne-leasing redova razrade cijene. */
const PRICE_ROWS: {
  label: string;
  detail?: string;
  priceKey: keyof CalculationPrices;
}[] = [
  { label: "admin.calculation.price.detail.tire.summer", priceKey: "tireSummer" },
  {
    label: "admin.calculation.price.detail.tire.summer.change",
    detail: "4 puta u zadanom periodu",
    priceKey: "tireSummerChange",
  },
  { label: "admin.calculation.price.detail.tire.winter", priceKey: "tireWinter" },
  {
    label: "admin.calculation.price.detail.tire.winter.change",
    detail: "3 puta u zadanom periodu",
    priceKey: "tireWinterChange",
  },
  {
    label: "admin.calculation.price.detail.vehicle.services",
    priceKey: "vehicleServices",
  },
  {
    label: "admin.calculation.price.detail.vehicle.tehnical.inspection",
    priceKey: "vehicleTechnicalInspection",
  },
  {
    label: "admin.calculation.price.detail.vehicle.toll",
    priceKey: "vehicleToll",
  },
  {
    label: "admin.calculation.price.detail.vehicle.insurance",
    priceKey: "vehicleInsurance",
  },
  {
    label: "admin.calculation.price.detail.vehicle.comprehensive.insurance",
    priceKey: "vehicleComprehensiveInsurance",
  },
  {
    label: "admin.calculation.price.detail.vehicle.annual.tax",
    priceKey: "vehicleAnnualTax",
  },
  {
    label: "admin.calculation.price.detail.vehicle.rtvfee",
    priceKey: "vehicleRtvFee",
  },
  {
    label: "admin.calculation.price.detail.vehicle.fireextinguisher",
    priceKey: "vehicleFireExtinguisher",
  },
];

const CalculationPriceDetailCard: React.FC<CalculationPriceDetailCardProps> = (
  props
) => {
  const { formValues, setFormValues } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [leasings, setLeasings] =
    useState<DataWithPagination<LeasingRow>>(initialDataWithPagination);

  const { t } = useTranslate();

  useEffect(() => {
    fetchLeasingResult(leasings.pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeasingResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getLeasingsAPI(pagination, name);
      setLeasings(results);
    } catch {
      setLeasings(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleDropdownChange = useHandleDropdownChange<CalculationEditFormValues>(
    setFormValues,
    formValues
  );

  /** Custom handler — standardni hook ne podržava nested putanju do formValues.prices. */
  const handlePriceChange = (value: number | null, name?: string) => {
    if (!name) return;
    setFormValues((prev) => ({
      ...prev,
      prices: { ...prev.prices, [name]: value },
    }));
  };

  const leasingItems: DropdownOption[] = leasings.data.map((i) => ({
    value: i.id.toString(),
    text: i.name,
  }));

  const total = Object.values(formValues.prices).reduce(
    (sum, value) => sum + value,
    0
  );

  return (
    <>
      <div className="overflow-auto">
        <div className="pb-5 pt-6 px-4 flex justify-end gap-4 border-b border-b-slate-200">
          <div className="pr-2 flex-1 max-w-[300px] uppercase font-semibold text-xs  opacity-80 text-left">
            {t("admin.calculation.price.detail.product")}
          </div>
          <div className="pr-2 flex-1 w-1/2 uppercase font-semibold text-xs  opacity-80 text-left">
            {t("admin.calculation.price.detail.detail")}
          </div>
          <div className="pr-2 w-40 uppercase font-semibold text-xs  opacity-80 text-right">
            {t("admin.calculation.price.detail.price")}
          </div>
        </div>
        <div className="relative divide-y divide-slate-200">
          {/* Leasing — Dropdown za odabir leasinga + cijena */}
          <div className="flex items-center gap-4 py-2 px-3 border-l-2 border-[#eab308] border-r hover:bg-slate-50">
            <div className="w-1/3 font-semibold flex-1 text-sm">
              <div className="flex items-center gap-3">
                <div className="flex-1 max-w-[300px]">
                  {t("admin.calculation.price.detail.leasing")}
                </div>
                <div className="flex-1 max-w-xs">
                  <Dropdown
                    name="leasingId"
                    placeholder={t("admin.leasing.choose")}
                    options={leasingItems}
                    onChange={handleDropdownChange}
                    value={formValues.leasingId}
                  />
                </div>
              </div>
            </div>
            <div className="w-40">
              <NumberField
                name="leasing"
                onChange={handlePriceChange}
                value={formValues.prices.leasing}
                groupAppend="€"
              />
            </div>
          </div>

          {/* Ostale stavke razrade */}
          {PRICE_ROWS.map((row) => (
            <div
              key={row.priceKey}
              className="flex items-center gap-4 py-2 px-3 border-l-2 border-[#eab308] border-r hover:bg-slate-50"
            >
              <div className="w-1/3 flex-1 text-sm">
                {row.detail ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 max-w-[300px] opacity-80 font-normal">
                      {t(row.label)}
                    </div>
                    <div className="flex-1 max-w-xs">
                      <strong>{row.detail}</strong>
                    </div>
                  </div>
                ) : (
                  <div className="font-semibold">{t(row.label)}</div>
                )}
              </div>
              <div className="w-40">
                <NumberField
                  name={row.priceKey}
                  onChange={handlePriceChange}
                  value={formValues.prices[row.priceKey]}
                  groupAppend="€"
                />
              </div>
            </div>
          ))}
        </div>
        <div className="pb-5 pt-6 px-4 flex justify-end gap-4 border-t border-t-slate-200">
          <div className="pr-2 flex-1 w-1/3 uppercase font-semibold text-sm  opacity-80 text-left">
            {t("admin.calculation.total")}
          </div>
          <div className="pr-2 w-40 uppercase font-semibold text-sm  opacity-80 text-right">
            {formatCurrency.format(total)}
          </div>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(CalculationPriceDetailCard);
